import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/components/LanguageContext";
import SEO from "@/components/SEO";
import ScrollReveal from "@/components/ScrollReveal";
import PageHero from "@/components/PageHero";
import LeadCTA from "@/components/LeadCTA";
import EN from "@/data/content-en";
import ZH from "@/data/content-zh";
import { PAGE_IMAGES, pathFor } from "@/data/pages";

export default function ServicesPricingPage() {
  const { lang, t } = useLang();
  const c = lang === "zh" ? ZH["services-pricing"] : EN["services-pricing"];

  return (
    <div className={lang === "zh" ? "lang-zh" : ""}>
      <SEO pageId="services-pricing" title={c.title} description={c.metaDescription} image={PAGE_IMAGES["services-pricing"]} />

      <PageHero pageId="services-pricing" mainTitle={c.h1} subtitle={c.intro} image={PAGE_IMAGES["services-pricing"]} />

      <section className="bg-[var(--abs-cream)] py-16 md:py-20" data-testid="pricing-hero">
        <div className="max-w-[1480px] mx-auto px-6 lg:px-10">
          <ScrollReveal>
            <div className="mt-3 text-[12.5px] text-[var(--abs-mute)] italic">{t.misc.allPrices}</div>
          </ScrollReveal>
        </div>
      </section>

      <section id="price-grid" className="bg-white py-20" data-testid="pricing-grid">
        <div className="max-w-[1480px] mx-auto px-6 lg:px-10 space-y-20">
          {c.categories.map((cat, idx) => (
            <div key={cat.heading}>
              <ScrollReveal>
                <div className="flex items-end justify-between gap-6 mb-10 border-b border-[var(--abs-line)] pb-5">
                  <h2 className="serif text-3xl md:text-5xl">{cat.heading}</h2>
                  <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--abs-orange)]">0{idx + 1}</div>
                </div>
              </ScrollReveal>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {cat.items.map((item, j) => (
                  <ScrollReveal key={item.name} delay={j * 30} className="border border-[var(--abs-line)] p-7 hover:border-[var(--abs-orange)] lift-card bg-white">
                    {item.isAddOnCard ? (
                      <>
                        <h4 className="serif text-3xl leading-snug min-h-[58px] text-[var(--abs-orange)]" data-testid="pricing-addon-title">{item.name}</h4>
                        <ul className="mt-4 divide-y divide-[var(--abs-line)]">
                          {item.addOns.map((ao) => (
                            <li key={ao.name} className="py-3 flex items-baseline justify-between gap-3">
                              <div className="text-[14.5px] leading-snug">
                                <div className="text-[var(--abs-ink)]">{ao.name}</div>
                                {ao.desc && <div className="text-[12.5px] text-[var(--abs-mute)] mt-0.5">{ao.desc}</div>}
                              </div>
                              <span className="serif text-xl text-[var(--abs-orange)] whitespace-nowrap">{ao.price}</span>
                            </li>
                          ))}
                        </ul>
                      </>
                    ) : (
                      <>
                        <h4 className="serif text-[20px] leading-snug min-h-[58px]">{item.name}</h4>
                        <div className="mt-4 mb-4 flex items-baseline gap-1">
                          <span className="serif text-3xl text-[var(--abs-orange)]">{item.price}</span>
                        </div>
                        <p className="text-[14px] leading-relaxed text-[var(--abs-mute)]">{item.desc}</p>
                      </>
                    )}
                  </ScrollReveal>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[var(--abs-beige)] py-24 text-center">
        <div className="max-w-3xl mx-auto px-6">
          <ScrollReveal>
            <p className="serif text-3xl md:text-4xl leading-snug">"{t.misc.quote}"</p>
            <div className="mt-5 text-[12.5px] uppercase tracking-[0.22em] text-[var(--abs-orange)]">— {t.misc.quoteAuthor}</div>
            <Link to={pathFor("contact", lang)} className="abs-btn mt-10 inline-flex" data-testid="pricing-cta">
              {t.cta.schedule} <ArrowRight className="h-4 w-4 arrow" />
            </Link>
          </ScrollReveal>
        </div>
      </section>

      <LeadCTA />
    </div>
  );
}
